import React from 'react';
import { Todo } from '../../types/todo';
import { useCategories } from '../../context/CategoriesContext';

interface TodoCardProps {
  todo: Todo;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (todo: Todo) => void;
  onViewDetails: (todo: Todo) => void;
}

const TodoCard: React.FC<TodoCardProps> = ({ todo, onToggle, onDelete, onEdit, onViewDetails }) => {
  const { categories } = useCategories();
  const priorityColors = {
    low: 'text-green-600 font-medium',
    medium: 'text-blue-600 font-medium',
    high: 'text-orange-600 font-medium',
    urgent: 'text-red-600 font-bold',
  };

  // Due date helper functions
  const formatDueDate = (dueDateStr: string | null) => {
    if (!dueDateStr) return null;
    
    // Create dates without timezone conversion issues
    const dueDate = new Date(dueDateStr);
    const today = new Date();
    
    // Get just the date parts (ignoring time)
    const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    const diffTime = dueDateOnly.getTime() - todayOnly.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    
    // Always show the actual date for future dates
    return dueDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: dueDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  const getDueDateStyle = (dueDateStr: string | null) => {
    if (!dueDateStr) return '';
    
    const dueDate = new Date(dueDateStr);
    const today = new Date();
    
    const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    if (dueDateOnly < todayOnly) return 'text-red-600 font-bold'; // Overdue
    if (dueDateOnly.getTime() === todayOnly.getTime()) return 'text-orange-600 font-medium'; // Today
    return 'text-gray-600'; // Future
  };

  const isOverdue = (dueDateStr: string | null) => {
    if (!dueDateStr || todo.completed) return false;
    
    const dueDate = new Date(dueDateStr);
    const today = new Date();
    
    const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    return dueDateOnly < todayOnly;
  };


  const getCategoryDisplay = (categoryId: string) => {
    const category = categories.find((c: any) => c.id === categoryId);
    if (!category) return { name: categoryId, classes: 'bg-gray-100 text-gray-700' };
    
    const colorMap: { [key: string]: string } = {
      slate: 'bg-slate-500 text-white',
      gray: 'bg-gray-500 text-white',
      red: 'bg-red-500 text-white',
      orange: 'bg-orange-500 text-white',
      amber: 'bg-amber-500 text-white',
      yellow: 'bg-yellow-400 text-gray-900',
      lime: 'bg-lime-500 text-white',
      green: 'bg-green-500 text-white',
      emerald: 'bg-emerald-500 text-white',
      teal: 'bg-teal-500 text-white',
      cyan: 'bg-cyan-500 text-white',
      blue: 'bg-blue-500 text-white',
      indigo: 'bg-indigo-500 text-white',
      violet: 'bg-violet-500 text-white',
      purple: 'bg-purple-500 text-white',
      pink: 'bg-pink-500 text-white',
    };
    
    return {
      name: category.name,
      classes: colorMap[category.color] || 'bg-gray-100 text-gray-700'
    };
  };

  return (
    <div className={`bg-white border-b border-gray-200 py-2 px-4 hover:bg-gray-50 transition-colors select-none ${
      isOverdue(todo.dates.due) ? 'border-l-4 border-l-red-500' : ''
    }`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={todo.completed}
          onChange={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onToggle(todo._id);
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer flex-shrink-0"
          title={todo.completed ? "Mark as pending" : "Mark as complete"}
        />
        <div className="flex-1 min-w-0 text-left">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className={`text-xs px-1.5 py-0.5 rounded ${getCategoryDisplay(todo.category).classes}`}>
                  {getCategoryDisplay(todo.category).name}
                </span>
                <span className={`text-xs ${priorityColors[todo.priority]}`}>
                  {todo.priority}
                </span>
                {todo.dates.received && (
                  <div className="flex items-center gap-1">
                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs text-gray-500">
                      {new Date(todo.dates.received).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        year: new Date(todo.dates.received).getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                      })}
                    </span>
                  </div>
                )}
                {todo.dates.due && (
                  <div className="flex items-center gap-1">
                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className={`text-xs ${getDueDateStyle(todo.dates.due)}`}>
                      {formatDueDate(todo.dates.due)}
                    </span>
                  </div>
                )}
              </div>
              <h3
                className={`text-sm leading-tight text-left cursor-pointer hover:text-blue-600 transition-colors ${todo.completed ? 'line-through text-gray-500 hover:text-gray-700' : 'text-gray-900'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetails(todo);
                }}
                title="Click to view details"
              >
                {todo.title}
              </h3>
              {todo.description && todo.description.trim() !== '' &&
               !todo.description.includes('Email from') && (
                <p
                  className={`text-xs leading-tight mt-0.5 text-left cursor-pointer hover:text-blue-600 transition-colors ${todo.completed ? 'text-gray-400 hover:text-gray-600' : 'text-gray-600'} line-clamp-1`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails(todo);
                  }}
                  title="Click to view details"
                >
                  {todo.description}
                </p>
              )}
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button
                onClick={() => onViewDetails(todo)}
                className="text-gray-400 hover:text-green-600 transition-colors p-0.5"
                title="View Details"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              <button
                onClick={() => onEdit(todo)}
                className="text-gray-400 hover:text-blue-600 transition-colors p-0.5"
                title="Edit"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={() => onDelete(todo._id)}
                className="text-gray-400 hover:text-red-600 transition-colors p-0.5"
                title="Delete"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodoCard;